import { useCallback } from 'react';
import { actions, useAppDispatch, useAppSelector } from '@/utils';
import { ITableColumnsConfig, ITableSortConfig } from '@/store/tables/types';

// ==============================|| GLOBAL TABLE - COLUMN CONFIGURATION (redux) ||============================== //
// The table configuration (column visibility + active sort + manually dragged widths +
// left/right pinning) lives in the `tables` redux slice, under the table's key — NOT in
// separate localStorage keys (the slice is persisted by redux-persist under the store's
// single key and cleared at logout).

// Table key → the route that identifies it. By default CustomTable automatically uses
// the current route (location.pathname); the entries here are for tables that need a
// FIXED key — e.g. tables rendered on routes containing ids (/administrations/:id),
// where the configuration must be shared across all profiles instead of saved per id.
export const TABLE_KEYS = {
	// Users of an administration (tab inside the FSP profile) — shared across administrations.
	administrationUsers: '/common/users/fsp/:id/users',
	// A citizen's subscriptions (tab inside the citizen profile) — shared across citizens.
	userSubscriptions: '/common/users/app/:id/subscriptions',
	// The projects table (the tabular view inside the projects list).
	projects: '/common/project/table',
	// Content tabs on the project detail page (route contains the project id) —
	// the configuration is shared across projects.
	projectAnnouncements: '/common/project/:id/anunturi',
	projectSurveys: '/common/project/:id/consultari'
} as const;

// The configuration comes from redux-persist (localStorage) — external, potentially
// corrupted input (hand-edited, or left behind by an older slice schema). Without
// validation an unexpected shape (e.g. a non-array `pinned.left`) would throw a
// TypeError at mount and take the whole page down. Only values with the correct shape
// and type are kept.
export const sanitizeTableConfig = (
	raw: ITableColumnsConfig | undefined
): ITableColumnsConfig => {
	if (!raw || typeof raw !== 'object') return {};
	const out: ITableColumnsConfig = {};

	const booleanRecord = (rec: unknown): Record<string, boolean> | undefined => {
		if (!rec || typeof rec !== 'object') return undefined;
		const entries = Object.entries(rec as Record<string, unknown>).filter(
			([, value]) => typeof value === 'boolean'
		);
		return entries.length > 0
			? (Object.fromEntries(entries) as Record<string, boolean>)
			: undefined;
	};

	const positiveNumberRecord = (rec: unknown): Record<string, number> | undefined => {
		if (!rec || typeof rec !== 'object') return undefined;
		const entries = Object.entries(rec as Record<string, unknown>).filter(
			([, value]) => typeof value === 'number' && Number.isFinite(value) && value > 0
		);
		return entries.length > 0
			? (Object.fromEntries(entries) as Record<string, number>)
			: undefined;
	};

	const stringArray = (arr: unknown): string[] =>
		Array.isArray(arr)
			? arr.filter((item): item is string => typeof item === 'string')
			: [];

	out.visibility = booleanRecord(raw.visibility);
	out.userWidths = positiveNumberRecord(raw.userWidths);
	out.pinnedWidths = positiveNumberRecord(raw.pinnedWidths);

	const sort = raw.sort as ITableSortConfig | undefined;
	if (
		sort &&
		typeof sort.field === 'string' &&
		(sort.direction === 'asc' || sort.direction === 'desc')
	) {
		out.sort = { field: sort.field, direction: sort.direction };
	}

	if (raw.pinned && typeof raw.pinned === 'object') {
		const left = stringArray(raw.pinned.left);
		const right = stringArray(raw.pinned.right);
		if (left.length > 0 || right.length > 0) out.pinned = { left, right };
	}

	return out;
};

export interface UseTableConfigReturn {
	config: ITableColumnsConfig | undefined;
	saveConfig: (next: ITableColumnsConfig) => void;
	clearConfig: () => void;
}

export function useTableConfig(key: string): UseTableConfigReturn {
	const dispatch = useAppDispatch();
	const config = useAppSelector((state) => state.tables.configs[key]);

	const saveConfig = useCallback(
		(next: ITableColumnsConfig): void => {
			dispatch(actions.setTableConfig({ key, config: next }));
		},
		[dispatch, key]
	);

	const clearConfig = useCallback((): void => {
		dispatch(actions.clearTableConfig(key));
	}, [dispatch, key]);

	return { config, saveConfig, clearConfig };
}
